import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Zap, MapPin, Building2, Globe2, Layers, MessageCircle, Smartphone, Cable, Headphones, Watch, BatteryCharging, ShieldEllipsis, Boxes } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

// Hero product visual — Blackview XPLORE 6, the latest announced Blackview flagship.
// Sourced from the public industry-catalog CDN used by trade press.
// NOTE: not in inventory; this is a visibility placement for the announced flagship only.
const HERO_PRODUCT_IMG = "/assets/xplore6-01.jpg";

const CATEGORY_CARDS = [
  { id: "iphone", label: { en: "iPhone", ar: "آيفون" }, icon: Smartphone, to: "/phones?category=iphone", img: "https://images.unsplash.com/photo-1512054502232-10a0a035d672?w=800" },
  { id: "samsung", label: { en: "Samsung", ar: "سامسونج" }, icon: Smartphone, to: "/phones?category=samsung", img: "https://images.pexels.com/photos/214488/pexels-photo-214488.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { id: "other_phone", label: { en: "Other Brands", ar: "علامات أخرى" }, icon: Smartphone, to: "/phones?category=other_phone", img: "https://images.unsplash.com/photo-1598327105666-5b89351aff97?w=800" },
  { id: "chargers", label: { en: "Chargers", ar: "شواحن" }, icon: BatteryCharging, to: "/accessories?category=chargers", img: "https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=800" },
  { id: "cables", label: { en: "Cables", ar: "كابلات" }, icon: Cable, to: "/accessories?category=cables", img: "https://images.unsplash.com/photo-1600490722773-35753aea6332?w=800" },
  { id: "earbuds", label: { en: "Earbuds", ar: "سماعات" }, icon: Headphones, to: "/accessories?category=earbuds", img: "https://images.unsplash.com/photo-1572569511254-d8f925fe2cbb?w=800" },
  { id: "cases", label: { en: "Cases", ar: "أغلفة" }, icon: ShieldEllipsis, to: "/accessories?category=cases", img: "https://images.unsplash.com/photo-1601593346740-925612772716?w=800" },
  { id: "power_banks", label: { en: "Power Banks", ar: "بطاريات محمولة" }, icon: BatteryCharging, to: "/accessories?category=power_banks", img: "https://images.unsplash.com/photo-1609091839311-d5365f9ff1c5?w=800" },
  { id: "smart_watches", label: { en: "Smart Watches", ar: "ساعات ذكية" }, icon: Watch, to: "/accessories?category=smart_watches", img: "https://images.pexels.com/photos/5083218/pexels-photo-5083218.jpeg?auto=compress&cs=tinysrgb&w=800" },
  { id: "other_accessory", label: { en: "Other Accessories", ar: "إكسسوارات أخرى" }, icon: Boxes, to: "/accessories?category=other_accessory", img: "https://images.unsplash.com/photo-1587033411391-5d9e51cce126?w=800" },
];

// Positions (percentages) for the global market network dots
const MARKETS = [
  { key: "UAE",         style: { left: "62%", top: "56%" } },
  { key: "Middle East", style: { left: "58%", top: "48%" } },
  { key: "Europe",      style: { left: "48%", top: "30%" } },
  { key: "Asia",        style: { left: "78%", top: "42%" } },
  { key: "Africa",      style: { left: "50%", top: "68%" } },
];
const DUBAI_POS = { left: "62%", top: "56%" };

export default function HomePage() {
  const { tr, lang, settings } = useApp();
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [hotPicks, setHotPicks] = useState([]);
  useEffect(() => { document.title = "AFC TELECOM | Technology Distribution — Dubai"; }, []);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => {
        if (r.data.length) {
          setFeatured(r.data.slice(0, 8));
        } else {
          api.get("/products", { params: { type: "phone" } }).then((r2) => setFeatured(r2.data.slice(0, 8))).catch(() => {});
        }
      })
      .catch(() => {});
    api.get("/products", { params: { new_arrival: true, type: "phone" } })
      .then((r) => setNewArrivals(r.data.slice(0, 8)))
      .catch(() => {});
    api.get("/products", { params: { hot_deal: true, type: "phone" } })
      .then((r) => setHotDeals(r.data.slice(0, 8)))
      .catch(() => {});
    api.get("/products", { params: { hot_pick: true, type: "phone" } })
      .then((r) => setHotPicks(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.general)}`;

  return (
    <div>
      {/* Hero */}
      <section className="hero-bg relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-semibold text-[#1E5FD9] tracking-[0.15em] uppercase mb-6">
              <MapPin className="w-3.5 h-3.5" /> {tr.hero.badge}
            </div>
            <div className="inline-flex items-center gap-2 ms-2 mb-6 px-3 py-1 rounded-full bg-[#1E5FD9] text-white text-[11px] font-bold tracking-[0.2em] uppercase shadow-[0_8px_24px_-10px_rgba(30,95,217,0.6)]" data-testid="hero-blackview-pill">
              <ShieldCheck className="w-3.5 h-3.5" /> {lang === "ar" ? "موزع Blackview المعتمد" : "Blackview Authorized Distributor"}
            </div>
            <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-[#0B132B] leading-[1.05]" data-testid="hero-headline">
              {(lang === "ar" ? settings.hero_headline_ar : settings.hero_headline_en) ||
                (lang === "ar" ? "التوزيع العالمي للهواتف المحمولة. من دبي." : "Global Mobile Distribution. Connected from Dubai.")}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              {(lang === "ar" ? settings.hero_sub_ar : settings.hero_sub_en) ||
                (lang === "ar"
                  ? "توزيع احترافي بين الشركات لأبرز العلامات التجارية مع شركاء الجملة في الأسواق الإقليمية والدولية."
                  : "Professional B2B mobile phone distribution connecting trusted brands with wholesale partners across regional and international markets.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] px-7 py-6 text-base">
                <Link to="/phones" data-testid="hero-cta-explore">{(lang === "ar" ? settings.hero_cta1_ar : settings.hero_cta1_en) || tr.hero.cta1} <ArrowRight className="w-4 h-4 ms-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-slate-300 hover:border-[#1E5FD9] hover:text-[#1E5FD9] px-7 py-6 text-base">
                <Link to="/request-quote" data-testid="hero-cta-quote">{(lang === "ar" ? settings.hero_cta2_ar : settings.hero_cta2_en) || tr.hero.cta2}</Link>
              </Button>
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE57] text-white px-6 py-3 text-sm font-medium" data-testid="hero-cta-whatsapp">
                <MessageCircle className="w-4 h-4" /> {tr.common.whatsappSales}
              </a>
            </div>
          </div>

          <div className="relative fade-up stagger-2" data-testid="hero-product-visual">
            <div className="absolute -inset-6 silver-metallic rounded-[2.5rem] blur-2xl opacity-40" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(30,95,217,0.5)] border border-slate-200 dark:border-[#1E2A4A] bg-gradient-to-br from-white to-slate-100 dark:from-[#101833] dark:to-[#0A1024]">
              {/* Authorization chip — stays the strongest message on the hero */}
              <div className="absolute top-5 start-5 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E5FD9] text-white text-[10px] font-bold tracking-[0.18em] uppercase shadow-[0_8px_24px_-10px_rgba(30,95,217,0.7)]" data-testid="hero-auth-chip">
                <ShieldCheck className="w-3.5 h-3.5" /> Blackview · Authorized
              </div>
              {/* "Just announced" indicator — we are NOT claiming stock */}
              <div className="absolute top-5 end-5 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/95 text-[#0B132B] text-[10px] font-bold tracking-[0.18em] uppercase" data-testid="hero-announced-chip">
                <Zap className="w-3 h-3" /> {lang === "ar" ? "جديد مُعلن" : "Just Announced"}
              </div>
              {/* Latest flagship model label */}
              <div className="absolute bottom-5 end-5 z-10 text-end">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold" data-testid="hero-model-tag">{lang === "ar" ? "أحدث طراز رائد" : "Latest Flagship"}</p>
                <p className="font-display text-lg sm:text-xl font-bold text-[#0B132B] dark:text-white leading-tight" data-testid="hero-model-name">XPLORE 6</p>
              </div>
              <img
                src={HERO_PRODUCT_IMG}
                alt="Blackview XPLORE 6 — latest announced flagship from Blackview · AFC TELECOM Authorized Distributor"
                className="w-full h-auto max-h-[460px] object-contain p-8 sm:p-12 mix-blend-multiply dark:mix-blend-normal dark:rounded-[2rem]"
                loading="eager"
                data-testid="hero-product-img"
              />
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(30,95,217,0.08),transparent_55%)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Hot Picks — shown at the top of the homepage, below the hero */}
      {hotPicks.length > 0 && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="hot-picks-section">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">★ {lang === "ar" ? "أبرز العروض" : "Hot Picks"}</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{lang === "ar" ? "اختيارات هذا الأسبوع" : "This Week's Hot Picks"}</h2>
            <p className="mt-2 text-slate-500 max-w-xl">{lang === "ar" ? "طرازات بارزة اختارها فريق المبيعات لدينا — موزع Blackview المعتمد." : "Flagship models hand-picked by our sales desk — Blackview Authorized Distributor."}</p>
          </div>
          <Link to="/phones?brand=Blackview" className="text-sm font-medium text-[#1E5FD9] hover:text-[#174AB0]">{lang === "ar" ? "جميع Blackview" : "All Blackview"} →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotPicks.map((p) => (
            <div key={p.id} data-testid={`hot-pick-${p.id}`}>
              <ProductCard product={p} showSpecs />
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Dubai Hub */}
      {settings.show_dubai_hub !== false && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="dubai-hub-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">01 / Dubai Hub</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{tr.home.dubaiTitle}</h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{tr.home.dubaiSub}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 text-sm"><Building2 className="w-4 h-4 text-[#1E5FD9]" /> DAFZA W5</div>
              <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 text-sm"><Globe2 className="w-4 h-4 text-[#1E5FD9]" /> UAE</div>
              <div className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 text-sm"><Layers className="w-4 h-4 text-[#1E5FD9]" /> B2B Distribution</div>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg">
              <img src="https://images.unsplash.com/photo-1726533765829-67f0313ab064?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGJ1c2luZXNzJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzg4MDAzMjYzfDA&ixlib=rb-4.1.0&q=85" alt="Dubai business district" className="w-full h-full object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Global Network */}
      {settings.show_network !== false && (
      <section className="bg-[#0B132B] text-white py-20" data-testid="network-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3B82F6]">02 / Network</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold">{tr.home.networkTitle}</h2>
          <p className="mt-3 text-slate-400 max-w-2xl">{tr.home.networkSub}</p>

          <div className="mt-10 relative aspect-[16/9] rounded-3xl border border-white/10 overflow-hidden bg-gradient-to-br from-[#0B132B] via-[#0f1a3a] to-[#0B132B]">
            {/* World map (svg outline) */}
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-[0.14] invert"
              aria-hidden="true"
            />
            {/* connection lines from Dubai to each market */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {MARKETS.filter((m) => m.key !== "UAE").map((m, i) => {
                const x1 = parseFloat(DUBAI_POS.left); const y1 = parseFloat(DUBAI_POS.top);
                const x2 = parseFloat(m.style.left); const y2 = parseFloat(m.style.top);
                return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="rgba(59,130,246,0.55)" strokeWidth="0.2" strokeDasharray="0.6 0.6" />;
              })}
            </svg>
            {/* market dots */}
            {MARKETS.map((m, i) => (
              <div key={m.key} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2" style={m.style}>
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#3B82F6] opacity-60" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-[#3B82F6]" />
                </span>
                <span className="text-[10px] sm:text-xs uppercase tracking-[0.15em] text-slate-200 bg-white/5 border border-white/10 rounded-full px-2 py-0.5 backdrop-blur">
                  {tr.markets[i] || m.key}
                </span>
              </div>
            ))}
            {/* Dubai big node */}
            <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={DUBAI_POS}>
              <div className="w-5 h-5 rounded-full bg-white shadow-[0_0_0_6px_rgba(255,255,255,0.15)]" />
              <span className="mt-2 text-xs font-semibold text-white bg-[#1E5FD9] rounded-full px-3 py-1">DUBAI</span>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Product Categories (now Shop by Brand) */}
      {settings.show_categories !== false && (      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="brands-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">03 / {lang === "ar" ? "العلامات" : "Our Brands"}</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{lang === "ar" ? "تسوق حسب العلامة" : "Shop by Brand"}</h2>
            <p className="mt-2 text-slate-500 max-w-lg">{lang === "ar" ? "علامات تجارية موثوقة نوزعها من دبي إلى العالم." : "Trusted brands we distribute from Dubai to the world."}</p>
          </div>
          <Link to="/phones" className="text-sm font-medium text-[#1E5FD9] hover:text-[#174AB0]" data-testid="brands-see-all">{lang === "ar" ? "جميع الهواتف" : "All Phones"} →</Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
          {[
            { id: "Blackview", label: "Blackview", priority: true, tag: lang === "ar" ? "موزع معتمد" : "Authorized" },
            { id: "Apple", label: "Apple" },
            { id: "Samsung", label: "Samsung" },
            { id: "Xiaomi", label: "Xiaomi" },
            { id: "TECNO", label: "TECNO" },
            { id: "Infinix", label: "Infinix" },
          ].map((b) => (
            <Link
              key={b.id}
              to={`/phones?brand=${b.id}`}
              data-testid={`brand-tile-${b.id.toLowerCase()}`}
              className={`group relative overflow-hidden rounded-2xl border transition-all p-5 flex flex-col justify-between h-40 card-lift ${
                b.priority
                  ? "bg-gradient-to-br from-[#1E5FD9] to-[#0B132B] text-white border-[#1E5FD9] shadow-[0_20px_40px_-20px_rgba(30,95,217,0.6)]"
                  : "bg-white border-slate-200 hover:border-[#1E5FD9]"
              }`}
            >
              <div className={`inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold ${
                b.priority ? "bg-white/15 text-white" : "bg-slate-100 text-slate-500"
              }`}>
                {b.tag || (lang === "ar" ? "علامة" : "Brand")}
              </div>
              <div>
                <h3 className={`font-display text-2xl font-bold ${b.priority ? "text-white" : "text-[#0B132B]"}`}>{b.label}</h3>
                <span className={`mt-1 text-xs inline-flex items-center gap-1 ${b.priority ? "text-slate-200" : "text-slate-500"}`}>
                  {lang === "ar" ? "عرض الهواتف" : "View phones"} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      )}

      {/* B2B */}
      {settings.show_b2b !== false && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="b2b-section">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B132B] to-[#173a7a] text-white p-8 sm:p-12 lg:p-16 border border-[#1E5FD9]/30">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8ab4ff]">04 / {lang === "ar" ? "موزع معتمد" : "Authorized Distributor"}</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold">BLACKVIEW</h2>
            <p className="mt-1 font-display text-xl sm:text-2xl text-[#8ab4ff]">{lang === "ar" ? "موزع معتمد" : "Authorized Distributor"}</p>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {lang === "ar"
                ? "توزيع احترافي لأجهزة Blackview بالجملة من دبي إلى الأسواق الإقليمية والدولية."
                : "Professional B2B distribution of Blackview devices from Dubai across regional and international markets."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="rounded-full bg-white text-[#0B132B] hover:bg-slate-100 px-7 py-6 text-base">
                <Link to="/phones?brand=Blackview" data-testid="blackview-cta">{lang === "ar" ? "استعرض Blackview" : "Explore Blackview"} <ArrowRight className="w-4 h-4 ms-1" /></Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-white/30 text-white hover:bg-white/10 px-6 py-6">
                <Link to="/request-quote" data-testid="blackview-quote">{lang === "ar" ? "طلب عرض سعر" : "Request a Quote"}</Link>
              </Button>
            </div>
          </div>
          <div className="absolute -end-24 -top-24 w-96 h-96 rounded-full bg-[#1E5FD9]/25 blur-3xl pointer-events-none" />
          <div className="absolute -end-16 -bottom-24 w-80 h-80 rounded-full bg-[#3B82F6]/20 blur-3xl pointer-events-none" />
        </div>
      </section>
      )}

      {/* New Arrivals */}
      {newArrivals.length > 0 && (
      <section className="bg-slate-50 py-20" data-testid="new-arrivals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">05 / {lang === "ar" ? "جديد" : "Just In"}</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{lang === "ar" ? "وصلت حديثًا" : "New Arrivals"}</h2>
            </div>
            <Link to="/phones" className="text-sm font-medium text-[#1E5FD9] hover:text-[#174AB0]">{lang === "ar" ? "عرض الكل" : "See all"} →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {newArrivals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>
      )}

      {/* Hot Deals */}
      {hotDeals.length > 0 && (
      <section className="py-20" data-testid="hot-deals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-rose-500">06 / {lang === "ar" ? "عروض" : "Hot Stock"}</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{lang === "ar" ? "أفضل العروض" : "Best Deals"}</h2>
            </div>
            <Link to="/request-quote" className="text-sm font-medium text-[#1E5FD9] hover:text-[#174AB0]">{lang === "ar" ? "اطلب عرض سعر" : "Request wholesale quote"} →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {hotDeals.map((p) => <ProductCard key={p.id} product={p} showSpecs />)}
          </div>
        </div>
      </section>
      )}

      {/* Featured products */}
      {settings.show_featured !== false && (
      <section className="bg-slate-50 py-20" data-testid="featured-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">05 / Featured</p>
              <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{tr.home.featured}</h2>
              <p className="mt-2 text-slate-500">{tr.home.featuredSub}</p>
            </div>
            <Link to="/phones" className="hidden md:inline-flex items-center gap-1 text-sm font-medium text-[#1E5FD9] hover:text-[#174AB0]" data-testid="see-all-phones">
              {tr.common.exploreAll} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p) => <ProductCard key={p.id} product={p} showSpecs />)}
          </div>
        </div>
      </section>
      )}

      {/* Why AFC */}
      {settings.show_why !== false && (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="why-section">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">06 / Why</p>
        <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold text-[#0B132B]">{tr.home.whyTitle}</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tr.home.whyPoints.map((pt, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-2xl p-8 card-lift">
              <div className="w-10 h-10 rounded-xl bg-[#1E5FD9]/10 text-[#1E5FD9] flex items-center justify-center mb-4">
                {i === 0 ? <MapPin className="w-5 h-5" /> : i === 1 ? <ShieldCheck className="w-5 h-5" /> : i === 2 ? <Layers className="w-5 h-5" /> : <Globe2 className="w-5 h-5" />}
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0B132B]">{pt.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{pt.d}</p>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="final-cta-section">
        <div className="rounded-3xl border border-slate-200 bg-white p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl font-semibold text-[#0B132B]">{tr.home.finalCta}</h3>
            <p className="mt-2 text-slate-500">{tr.home.finalCtaSub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] px-7 py-6">
              <Link to="/request-quote" data-testid="final-cta-quote">{tr.hero.cta2}</Link>
            </Button>
            <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE57] text-white px-6 py-3 text-sm font-medium" data-testid="final-cta-whatsapp">
              <MessageCircle className="w-4 h-4" /> {tr.common.whatsappSales}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
